import { useCallback, useMemo } from 'react';
import { useAppSelector } from '@src/core/hooks';
import { localeSelector } from '@src/stores/userPreferences';
import { currencySelector } from '@src/stores/globalParameters';
import { CurrencyInfo, getCurrencyInfo } from '@src/common/currency';

const formatAmount = (amountCents: number, currencyCode: string, locale: string): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  }).format(amountCents / 100);
};

export const useCurrencyFormat = () => {
  const locale = useAppSelector(localeSelector);
  const currency = useAppSelector(currencySelector);
  const currencyData = useMemo(() => getCurrencyInfo(currency), [currency]);
  const intlLocale = useMemo(() => currencyData.locale, [currencyData.locale]);

  const formatCents = useCallback(
    (cents: number, showSymbol = true): string => {
      if (showSymbol) {
        return formatAmount(cents, currency, intlLocale);
      }

      try {
        return new Intl.NumberFormat(intlLocale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(cents / 100);
      } catch {
        return (cents / 100).toFixed(2);
      }
    },
    [intlLocale, currency],
  );

  const formatCurrencyInfo = useCallback(
    (currencyInfo: CurrencyInfo | null | undefined): string => {
      if (!currencyInfo) {
        return '';
      }

      return formatAmount(currencyInfo.amountCents, currencyInfo.currency, intlLocale);
    },
    [intlLocale],
  );

  const formatOriginalAmount = useCallback(
    (currencyInfo: CurrencyInfo | null | undefined): string => {
      if (!currencyInfo) {
        return '';
      }

      return formatAmount(currencyInfo.originalAmountCents, currencyInfo.originalCurrency, intlLocale);
    },
    [intlLocale],
  );

  return {
    formatCents,
    formatCurrencyInfo,
    formatOriginalAmount,
    locale,
    currency,
  };
};

export default useCurrencyFormat;
